import { Button, Text } from '@noorden-insurtech/alba-design-system';
import type { NotificationViewModel } from '../config/notification-presenters';
import { NotificationItem } from './notification-item.component';

type NotificationPanelProps = {
  notifications: NotificationViewModel[];
  isLoading: boolean;
  errorMessage?: string;
  hasUnread: boolean;
  /**
   * Perfil que pode dar o feed por lido. Falso na seguradora, que acompanha o
   * feed de uma corretora — ver `useNotificationsScope`.
   */
  canMarkAsRead: boolean;
  isMarkingAll: boolean;
  onSelect: (_notification: NotificationViewModel) => void;
  onMarkAllAsRead: () => void;
  onSeeAll: () => void;
};

/**
 * Conteúdo do dropdown do sino. Apresentacional: toda decisão (o que buscar,
 * quando marcar como lida, para onde navegar) mora no container.
 */
export function NotificationPanel({
  notifications,
  isLoading,
  errorMessage,
  hasUnread,
  canMarkAsRead,
  isMarkingAll,
  onSelect,
  onMarkAllAsRead,
  onSeeAll,
}: NotificationPanelProps) {
  return (
    <>
      <div className='flex items-center justify-between border-b border-zinc-200 px-3 py-2'>
        <Text className='text-sm font-semibold text-alba-black-500'>
          Notificações
        </Text>
        {hasUnread && (
          <Button
            variant='ghost'
            size='small'
            loading={isMarkingAll}
            onClick={onMarkAllAsRead}
            className='px-1 text-xs text-alba-blue-600'
          >
            Marcar todas como lidas
          </Button>
        )}
      </div>

      {/*
        Sem isto, o perfil que só acompanha clica, é levado ao destino e vê o
        badge intacto — sem nada dizendo por quê. O clique não muda nada de
        propósito, e "de propósito" tem que estar escrito, em português de
        quem trabalha com seguro e não com software.
      */}
      {!canMarkAsRead && (
        <div className='border-b border-zinc-200 bg-alba-gray-50 px-3 py-2'>
          <Text className='text-[11px] leading-snug text-alba-gray-600'>
            Você está vendo as notificações desta corretora. Somente ela pode
            marcá-las como lidas, para que nenhum aviso desapareça antes de o
            corretor ver.
          </Text>
        </div>
      )}

      <div className='max-h-80 overflow-y-auto'>
        {isLoading ? (
          <div className='space-y-3 px-3 py-3'>
            {[1, 2, 3].map(index => (
              <div key={index} className='flex animate-pulse items-start gap-2'>
                <div className='h-6 w-6 rounded-full bg-alba-gray-200' />
                <div className='flex flex-1 flex-col gap-1.5'>
                  <div className='h-4 w-3/4 rounded bg-alba-gray-200' />
                  <div className='h-3 w-1/2 rounded bg-alba-gray-100' />
                </div>
              </div>
            ))}
          </div>
        ) : errorMessage ? (
          <div className='px-3 py-4 text-sm text-alba-red-600'>
            {errorMessage}
          </div>
        ) : notifications.length === 0 ? (
          <div className='px-3 py-6 text-center text-sm text-alba-gray-600'>
            Você não tem notificações.
          </div>
        ) : (
          notifications.map(notification => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onSelect={onSelect}
            />
          ))
        )}
      </div>

      <div className='border-t border-zinc-200 p-2'>
        <Button
          variant='ghost'
          size='small'
          onClick={onSeeAll}
          className='w-full text-sm'
        >
          Ver todas as notificações
        </Button>
      </div>
    </>
  );
}
